import React, { useState } from 'react';
import { TopUsersData } from '../types/analytics.types';
import './TopUsersTable.css';

interface TopUsersTableProps {
  data: TopUsersData;
  loading?: boolean;
  onUserClick?: (userId: string) => void;
}

type SortField = 'name' | 'user_id' | 'sessions' | 'first_session' | 'last_session' | 'avg_duration';
type SortDirection = 'asc' | 'desc';

const PAGE_SIZE = 20;

export const TopUsersTable: React.FC<TopUsersTableProps> = ({ data, loading, onUserClick }) => {
  const [sortField, setSortField] = useState<SortField>('sessions');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  if (loading) {
    return (
      <div className="top-users-table">
        <h4>Top Users by Activity</h4>
        <div className="loading-skeleton" style={{ height: '400px' }}></div>
      </div>
    );
  }

  if (!data || !data.users || data.users.length === 0) {
    return (
      <div className="top-users-table">
        <h4>Top Users by Activity</h4>
        <p className="no-data">No user data available</p>
      </div>
    );
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
    setCurrentPage(0);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(0);
  };

  // Filter and sort data
  const filteredData = data.users.filter(user => {
    const term = searchTerm.toLowerCase();
    const fullName = `${(user as any).first_name || ''} ${(user as any).last_name || ''}`.toLowerCase();
    return user.user_id.toLowerCase().includes(term) || fullName.includes(term);
  });

  const sortedData = [...filteredData].sort((a, b) => {
    let aVal: number | string;
    let bVal: number | string;

    switch (sortField) {
      case 'name':
        aVal = `${(a as any).first_name || ''} ${(a as any).last_name || ''}`.trim();
        bVal = `${(b as any).first_name || ''} ${(b as any).last_name || ''}`.trim();
        break;
      case 'user_id':
        aVal = a.user_id;
        bVal = b.user_id;
        break;
      case 'sessions':
        aVal = (a as any).session_count || 0;
        bVal = (b as any).session_count || 0;
        break;
      case 'first_session':
        aVal = (a as any).first_session || '';
        bVal = (b as any).first_session || '';
        break;
      case 'last_session':
        aVal = (a as any).last_session || '';
        bVal = (b as any).last_session || '';
        break;
      case 'avg_duration':
        aVal = (a as any).avg_duration_minutes || 0;
        bVal = (b as any).avg_duration_minutes || 0;
        break;
      default:
        aVal = (a as any).session_count || 0;
        bVal = (b as any).session_count || 0;
    }

    if (typeof aVal === 'string') {
      return sortDirection === 'asc'
        ? aVal.localeCompare(bVal as string)
        : (bVal as string).localeCompare(aVal);
    }

    return sortDirection === 'asc' ? aVal - (bVal as number) : (bVal as number) - aVal;
  });

  // Pagination
  const totalPages = Math.ceil(sortedData.length / PAGE_SIZE);
  const startIndex = currentPage * PAGE_SIZE;
  const endIndex = startIndex + PAGE_SIZE;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const canPrev = currentPage > 0;
  const canNext = currentPage < totalPages - 1;
  const handlePrev = () => canPrev && setCurrentPage(p => p - 1);
  const handleNext = () => canNext && setCurrentPage(p => p + 1);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '⇅';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatDuration = (minutes: number | null | undefined) => {
    if (minutes === null || minutes === undefined || minutes === 0) return 'N/A';
    if (minutes < 1) return `${Math.round(minutes * 60)}s`;
    if (minutes < 60) return `${minutes.toFixed(1)} min`;
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="top-users-table">
      <div className="table-header">
        <h4>Top Users by Activity</h4>
        <div className="table-controls">
          <input
            type="text"
            placeholder="Search by name or user ID..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
          <span className="result-count">
            Showing {sortedData.length} of {data.total_users} users
          </span>
        </div>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} className="sortable">
                Name {getSortIcon('name')}
              </th>
              <th onClick={() => handleSort('user_id')} className="sortable">
                User ID {getSortIcon('user_id')}
              </th>
              <th onClick={() => handleSort('sessions')} className="sortable">
                Session Count {getSortIcon('sessions')}
              </th>
              <th onClick={() => handleSort('avg_duration')} className="sortable">
                Avg Duration {getSortIcon('avg_duration')}
              </th>
              <th onClick={() => handleSort('first_session')} className="sortable">
                First Session {getSortIcon('first_session')}
              </th>
              <th onClick={() => handleSort('last_session')} className="sortable">
                Last Session {getSortIcon('last_session')}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((user, index) => {
              const userData = user as any;
              const globalRank = startIndex + index + 1;
              return (
                <tr
                  key={user.user_id}
                  onClick={() => onUserClick?.(user.user_id)}
                  className={onUserClick ? 'clickable' : ''}
                >
                  <td>
                    <span className="user-rank">#{globalRank}</span>
                    <strong>{[userData.first_name, userData.last_name].filter(Boolean).join(' ') || '—'}</strong>
                  </td>
                  <td>{user.user_id}</td>
                  <td>{userData.session_count?.toLocaleString() || 0}</td>
                  <td>{formatDuration(userData.avg_duration_minutes)}</td>
                  <td className="date-cell">{formatDate(userData.first_session)}</td>
                  <td className="date-cell">{formatDate(userData.last_session)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedData.length === 0 && searchTerm && (
        <p className="no-results">No users found matching "{searchTerm}"</p>
      )}

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button onClick={handlePrev} disabled={!canPrev} className="pagination-btn">
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button onClick={handleNext} disabled={!canNext} className="pagination-btn">
            Next
          </button>
        </div>
      )}
    </div>
  );
};
